/* eslint-disable valid-jsdoc */
import { ICloneable } from '../../contracts/cloneable.js';
import { IDataComparable } from '../../contracts/data-comparable.js';
import { isNotNullOrUndefined, isNullOrUndefined } from '../../utility/null-safety.js';
import { SerializationException } from '../../exceptions/serialization-exception.js';
import { Result } from '../../utility/result.js';
import { DatabaseIdAlphabet } from '../../utility/database-id-generator.js';
import { ErrorLike } from '../core/error-like.js';
import { DataModel, SerializationContext, SerializedData } from './data-model.js';
import { DataModelFactory } from './data-model-factory.js';
import { createSerializationContext } from './serialization-context-impl.js';
import { Language } from '../serializable/language.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { DataModelWithMetaData } from './data-model-with-meta-data.js';
import { TimeZone } from '../../time/timezone.js';

export function isSameList<T>(
    a: readonly T[] | null | undefined,
    b: readonly T[] | null | undefined,
    comparator: (a: T, b: T) => boolean = (a: T, b: T) => {
      return a === b;
    },
): boolean {
  if(isNullOrUndefined(a) && isNullOrUndefined(b)) {
    return true;
  }
  else if(isNullOrUndefined(a)) {
    return false;
  }
  else if(isNullOrUndefined(b)) {
    return false;
  }

  if(a.length !== b.length) {
    return false;
  }

  for(let i = 0; i < a.length; i++) {
    const same = comparator(a[i], b[i]);
    if(!same) {
      return false;
    }
  }

  return true;
}

export function isExactSameList<T>(
    a: readonly T[] | null | undefined,
    b: readonly T[] | null | undefined,
    comparator: (a: T, b: T) => boolean = (a: T, b: T) => {
      return a === b;
    },
): boolean {
  if(a === undefined && b === undefined) return true;
  if(a === null && b === null) return true;

  if(isNullOrUndefined(a)) return false;
  if(isNullOrUndefined(b)) return false;

  if(a.length !== b.length) return false;

  for(let i = 0; i < a.length; i++) {
    const same = comparator(a[i], b[i]);
    if(!same) return false;
  }

  return true;
}

export function isSameMap<K, V>(
    a: Map<K, V> | null | undefined,
    b: Map<K, V> | null | undefined,
    comparator: (lh: V, rh: V) => boolean
    = (lh: V, rh: V) => {
      return lh === rh;
    },
): boolean {
  if(a == null || b == null) {
    return a == b;
  }

  if(a.size !== b.size) {
    return false;
  }

  for(const [ka, va] of a) {
    const vb = b.get(ka);

    if(isNullOrUndefined(va) || isNullOrUndefined(vb)) {
      if(va === vb) {
        continue;
      }
      else {
        return false;
      }
    }

    const same = comparator(va, vb);
    if(!same) {
      return false;
    }
  }

  return true;
}

export function isSameData<T extends IDataComparable>(
    a: T | null | undefined,
    b: T | null | undefined,
): boolean {
  if(isNullOrUndefined(a) || isNullOrUndefined(b)) {
    return a === b;
  }

  return a.isSame(b);
}

export function cloneData<T extends ICloneable>(data: T): T {
  return data.clone();
}

export function cloneDataOrNull<T extends ICloneable>(data: T | null): T | null {
  return data?.clone() ?? null;
}

export function cloneDataOrNullOrUndefined<T extends ICloneable>(data: T | null | undefined): T | null | undefined {
  if(data === undefined) return undefined;
  if(data === null) return null;
  return data.clone();
}


export function cloneMap<K, V>(original: Map<K, V>, valueCloner?: (v: V) => V): Map<K, V> {
  const clone: Map<K, V> = new Map<K, V>();
  original.forEach((v, k) => {
    if(valueCloner) {
      clone.set(k, valueCloner(v));
    }
    else {
      clone.set(k, v);
    }
  });

  return clone;
}

export function cloneMapOrNull<K, V>(
    original: Map<K, V> | null | undefined,
    valueCloner?: (v: V) => V,
): Map<K, V> | null {
  if(isNullOrUndefined(original)) {
    return null;
  }

  return cloneMap(original, valueCloner);
}

export function cloneList<T>(original: readonly T[]): T[] {
  const clone: T[] = [];
  clone.push(...original);

  return clone;
}

export function cloneAsSet<T>(original: Iterable<T>): Set<T> {
  return new Set<T>(original);
}

export function cloneListOrNull<T>(original: T[] | null): T[] | null {
  if(isNotNullOrUndefined(original)) {
    return cloneList(original);
  }

  return null;
}

export function cloneListOrNullOrUndefined<T>(original: T[] | null | undefined): T[] | null | undefined {
  if(original === undefined) return undefined;
  if(original === null) return null;

  return cloneList(original);
}

export function cloneListOfCloneable<T extends ICloneable>(original: readonly T[]): T[] {
  return original.map((e) => e.clone());
}

export function convertList<T, D>(source: T[], converter: (e: T) => D): D[] {
  const list: D[] = [];
  for(let i = 0; i < source.length; i++) {
    list.push(converter(source[i]));
  }

  return list;
}

export function convertMapEntries<K, V, TK, TV>(
    map: Map<K, V>,
    converter: (k: K, v: V) => {key: TK, value: TV}
): Map<TK, TV> {
  const convertedMap: Map<TK, TV> = new Map<TK, TV>();

  map.forEach((v, k) => {
    const entry = converter(k, v);
    convertedMap.set(entry.key, entry.value);
  });

  return convertedMap;
}

export function convertMapKeys<TK, TV, RK>(
    map: Map<TK, TV>,
    converter: (k: TK) => RK
): Map<RK, TV> {
  const convertedMap: Map<RK, TV> = new Map<RK, TV>();

  map.forEach((v, k) => {
    const key = converter(k);
    convertedMap.set(key, v);
  });

  return convertedMap;
}

export function convertMapValues<TK, TV, RV>(
    map: Map<TK, TV>,
    converter: (v: TV) => RV
): Map<TK, RV> {
  const convertedMap: Map<TK, RV> = new Map<TK, RV>();

  map.forEach((v, k) => {
    const value = converter(v);
    convertedMap.set(k, value);
  });

  return convertedMap;
}

export function mapToData<K, V>(
    map: Map<K, V>,
    converter: (k: K, v: V) => {key: string | number, value: unknown},
): SerializedData {
  const data: SerializedData = {};
  map.forEach((v, k) => {
    const entry = converter(k, v);
    data[entry.key] = entry.value;
  });

  return data;
}

export function mapToDataOrNull<K, V>(
    map: Map<K, V> | null | undefined,
    converter: (k: K, v: V) => {key: string | number, value: unknown},
): SerializedData | null {
  if(isNullOrUndefined(map)) {
    return null;
  }

  return mapToData(map, converter);
}

export function convertFromLanguageMap(map: Map<Language, string>): SerializedData {
  return mapToData(map, (k, v) => ({ key: fromEnumAsString(k), value: v }));
}

export function convertFromLanguageMapOrNull(map: Map<Language, string> | null | undefined): SerializedData | null {
  return isNotNullOrUndefined(map) ? mapToData(map, (k, v) => ({ key: fromEnumAsString(k), value: v })) : null;
}

export function convertToLanguageMap(data: SerializedData, key: string): Map<Language, string> {
  return MapUtils.getMap(data, key, (k, v) => ({ key: toEnumFromString(k, Language), value: v as string }));
}

export function convertToLanguageMapOrNull(data: SerializedData, key: string): Map<Language, string> | null {
  return MapUtils.getMapOrNull(data, key, (k, v) => ({ key: toEnumFromString(k, Language), value: v as string }));
}

export function generateUniqueDatabaseId(options?: {length?: number, alphabet?: DatabaseIdAlphabet}): string {
  if(isNullOrUndefined(DataModelFactory.instance)) {
    throw new SerializationException('Data model factory is not initialized.');
  }

  return DataModelFactory.instance.databaseIdGenerator.generate(options);
}

export function isSerializedData(map: SerializedData, typeId: string | null = null): boolean {
  if(isNullOrUndefined(DataModelFactory.instance)) {
    throw new SerializationException('Data model factory is not initialized.');
  }

  return DataModelFactory.instance.isSerializedData(map, typeId);
}

export function toData<T extends DataModel>(model: T, context: SerializationContext | null = null): SerializedData {
  if(isNullOrUndefined(DataModelFactory.instance)) {
    throw new SerializationException('Data model factory is not initialized.');
  }

  return DataModelFactory.instance.toData(context ?? createSerializationContext(), model);
}

export function toDataList<T extends DataModel>(models: T[], context: SerializationContext | null = null): SerializedData[] {
  if(isNullOrUndefined(DataModelFactory.instance)) {
    throw new SerializationException('Data model factory is not initialized.');
  }

  const serializationContext = context ?? createSerializationContext();
  return models.map((model) => DataModelFactory.instance!.toData(serializationContext, model));
}

export function fromData<T extends DataModel>(map: SerializedData, context: SerializationContext | null = null): T {
  if(isNullOrUndefined(DataModelFactory.instance)) {
    throw new SerializationException('Data model factory is not initialized.');
  }

  return DataModelFactory.instance.fromData(context ?? createSerializationContext(), map);
}

export function fromDataWithMetaData<T extends DataModel>(
    map: SerializedData,
    context: SerializationContext | null = null
): DataModelWithMetaData<T> {
  if(isNullOrUndefined(DataModelFactory.instance)) {
    throw new SerializationException('Data model factory is not initialized.');
  }

  const lastUpdate = TimeZone.decodeDate(MapUtils.getInteger(map, '$last_update'));

  return new DataModelWithMetaData(
      lastUpdate,
      DataModelFactory.instance.fromData(context ?? createSerializationContext(), map)
  );
}

// eslint-disable-next-line max-len
export function toDataOrNull<T extends DataModel>(model: T | null, context: SerializationContext | null = null): SerializedData | null {
  return isNullOrUndefined(model) ? null : toData<T>(model, context);
}

// eslint-disable-next-line max-len
export function fromDataOrNull<T extends DataModel>(map: SerializedData | null, context: SerializationContext | null = null): T | null {
  return isNullOrUndefined(map) ? null : fromData<T>(map, context);
}

export function tryFromNonNullableData<T extends DataModel>(map: SerializedData | null, context: SerializationContext | null = null): Result<T, ErrorLike> {
  if(isNullOrUndefined(map)) {
    return Result.error('value is null');
  }

  try {
    return Result.ok(fromData<T>(map, context));
  }
  catch(error) {
    return Result.error(`${error}`);
  }
}

export function tryFromNullableData<T extends DataModel>(map: SerializedData | null, context: SerializationContext | null = null): Result<T | null, ErrorLike> {
  if(isNullOrUndefined(map)) {
    return Result.ok(null);
  }

  try {
    return Result.ok(fromData<T>(map, context));
  }
  catch(error) {
    return Result.error(`${error}`);
  }
}

/**
 * @deprecated Please use `tryFromNonNullableData` instead.
 */
export function fromDataSafe<T extends DataModel>(map: SerializedData | null, context: SerializationContext | null = null): Result<T, ErrorLike> {
  return tryFromNonNullableData(map, context);
}

/**
 * @deprecated Please use `tryFromNullableData` instead.
 */
export function exceptionSafeFromData<T extends DataModel>(map: SerializedData | null, context: SerializationContext | null = null): Result<T | null, ErrorLike> {
  return tryFromNullableData(map, context);
}
